import { useEffect, useRef, useState } from 'react';
import type { Message } from '../types';

/** True only when the browser positively reports no connection (SSR/unknown => online). */
export function isBrowserOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** Tracks `navigator.onLine` through the window `online`/`offline` events. */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => !isBrowserOffline());
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const update = (event: Event) => setOnline(event.type === 'online');
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    // Catch a change that happened between the initial render and this effect.
    setOnline(!isBrowserOffline());
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

/**
 * When the connection comes back, resends (once each, oldest first) the failed
 * messages that were queued because the browser was offline. Failures for any
 * other reason (server errors, timeouts) keep their manual Retry.
 * A reconnect during an existing send leaves queued messages for manual retry.
 */
export function useRetryOnReconnect(
  online: boolean,
  messages: Message[],
  retry: (messageId: string) => Promise<unknown>,
  busy = false
): void {
  const wasOnline = useRef(online);
  const latest = useRef({ messages, retry, busy });
  latest.current = { messages, retry, busy };
  const activeBatch = useRef<object | null>(null);
  useEffect(
    () => () => {
      activeBatch.current = null;
    },
    []
  );

  useEffect(() => {
    const reconnected = online && !wasOnline.current;
    wasOnline.current = online;
    // Connection events can repeat while the first retry is still pending. Starting another
    // batch would let its next message supersede that send and reorder the queue.
    if (!reconnected || activeBatch.current || latest.current.busy) return;
    const batch = {};
    activeBatch.current = batch;
    const ids = latest.current.messages
      .filter(m => m.status === 'failed' && m.retryOnReconnect)
      .map(m => m.id);
    void (async () => {
      try {
        // Sequential: a new send supersedes (aborts) one still in flight.
        for (const id of ids) {
          if (activeBatch.current !== batch || isBrowserOffline()) return;
          try {
            const result = await latest.current.retry(id);
            // A newer send, conversation change, or Stop interrupted this batch.
            if (result && typeof result === 'object' && 'aborted' in result && result.aborted)
              return;
          } catch {
            // A host retry callback may reject; leave remaining messages for manual retry.
            return;
          }
        }
      } finally {
        if (activeBatch.current === batch) activeBatch.current = null;
      }
    })();
  }, [online]);
}
